import React from 'react';
import moment from 'moment';
import Avatar from 'react-avatar';
import { CheckCircle, Edit3, Trash2, ArrowUpDown, X, RotateCcw, Eye, Search, Filter } from 'lucide-react';

import RelativeTime from 'components/common/RelativeTime';
import axios from 'helpers/axios';
import history from 'helpers/history';
import config from 'helpers/config';
import RepairOrder from 'models/RepairOrderModel';
import TableColumn from 'models/TableColumnModel';
import dateFormats from 'helpers/dateFormats';
import Price from 'components/common/Price';

interface Props {
    onRepairOrdersCountChange?: (count: number) => void;
}

interface State {
    repairOrders: RepairOrder[];
    tableColumns: TableColumn[];
}

class DeletedRepairOrdersPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            repairOrders: [],
            tableColumns: repairOrderListColumns,
        };
    }

    componentDidMount() {
        this.getRepairOrders(1);
    }

    getRepairOrders(page: number, itemsPerPage = 50) {
        axios
            .get(`${config.apiUrl}/repairOrder`, { params: { page, itemsPerPage, archivedOnly: true } })
            .then((res) => {
                this.setState({ repairOrders: res.data.rows });
                if (this.props.onRepairOrdersCountChange) {
                    this.props.onRepairOrdersCountChange(res.data.count);
                }
            })
            .catch((_) => {
                console.log(_);
            });
    }

    isColumnActive(columnName: string) {
        return this.state.tableColumns.find((column) => column.name === columnName)?.active;
    }
    render() {
        const { repairOrders, tableColumns } = this.state;

        return (
            <div className="space-y-6">
                {/* Search and Filter Section */}
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    <div className="flex-1 max-w-md">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Search deleted repair orders..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            />
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                            <Filter className="h-4 w-4" />
                            <span className="text-sm">Filter</span>
                        </button>
                    </div>
                </div>

                {/* Results Summary */}
                <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">{repairOrders.length} deleted repair orders found</p>
                </div>

                {/* Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {tableColumns.map(
                                        (column, index) =>
                                            column.active && (
                                                <th
                                                    key={index}
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                >
                                                    <button className="flex items-center space-x-1 hover:text-gray-700 transition-colors group">
                                                        <span>{column.text}</span>
                                                        {column.sortable && (
                                                            <ArrowUpDown className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                        )}
                                                    </button>
                                                </th>
                                            ),
                                    )}
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>

                            <tbody className="bg-white divide-y divide-gray-200">
                                {repairOrders?.map((RO) => (
                                    <tr
                                        key={RO.id}
                                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                                        onClick={() => {
                                            history.push(`/repairOrder/${RO.id}`);
                                        }}
                                    >
                                        {this.isColumnActive('repairOrderNumber') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm font-medium text-blue-600">
                                                    #{RO.repairOrderNumber}
                                                </span>
                                            </td>
                                        )}
                                        {this.isColumnActive('orderName') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm font-medium text-gray-900">{RO.title}</span>
                                            </td>
                                        )}
                                        {this.isColumnActive('customer') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="space-y-1">
                                                    <div className="text-sm text-gray-900">
                                                        {RO.vehicle ? (
                                                            <span>
                                                                {RO.vehicle.year} {RO.vehicle.make} {RO.vehicle.model}
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400 italic">No vehicle</span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-600">
                                                        {RO.customer ? (
                                                            <span>
                                                                {RO.customer.firstName} {RO.customer.lastName}
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400 italic">No customer</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        )}
                                        {this.isColumnActive('total') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm font-medium text-gray-900">
                                                    <Price
                                                        price={
                                                            RO?.services?.reduce(
                                                                (acc, service) => acc + service.grandTotalCents,
                                                                0,
                                                            ) / 100
                                                        }
                                                    />
                                                </span>
                                            </td>
                                        )}
                                        {this.isColumnActive('due') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {RO?.dueDate && (
                                                    <span className="text-sm text-gray-900">
                                                        {moment(RO.dueDate).format(dateFormats.USA_DATETIME)}
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        {this.isColumnActive('paidStatus') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {Math.random() < 0.5 ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        Paid
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                        Unpaid
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        {this.isColumnActive('workflow') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-900">{RO.workflowStatus?.name}</span>
                                            </td>
                                        )}
                                        {this.isColumnActive('inspectionStatus') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-900">{RO.workflowStatus?.name}</span>
                                            </td>
                                        )}
                                        {this.isColumnActive('orderStatus') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {RO?.status === 1 ? 'Invoice' : 'Estimate'}
                                                </span>
                                            </td>
                                        )}
                                        {this.isColumnActive('authorizationStatus') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <CheckCircle className="h-5 w-5 text-green-500" />
                                            </td>
                                        )}
                                        {this.isColumnActive('technicians') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex -space-x-1">
                                                    {RO?.asignees?.length > 0 &&
                                                        RO.asignees.map((asignee, index) => (
                                                            <Avatar
                                                                key={index}
                                                                name={`${asignee.firstName} ${asignee.lastName}`}
                                                                round
                                                                size={'22'}
                                                                className="ring-2 ring-white"
                                                            />
                                                        ))}
                                                </div>
                                            </td>
                                        )}
                                        {this.isColumnActive('dateCreated') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="space-y-1">
                                                    <div className="text-sm text-gray-900">
                                                        <RelativeTime dateTime={RO.createdAt} />
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {moment(RO.createdAt).format(dateFormats.USA_DATETIME)}
                                                    </div>
                                                </div>
                                            </td>
                                        )}
                                        {this.isColumnActive('labels') && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-wrap gap-1">
                                                    {RO.labels?.map((label, indx) => (
                                                        <span
                                                            key={indx}
                                                            style={{ backgroundColor: label.color }}
                                                            className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-white"
                                                        >
                                                            <X className="h-3 w-3 mr-1" />
                                                            {label.title}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                        )}

                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation();
                                                        history.push(`/repairOrder/${RO.id}`);
                                                    }}
                                                    className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                    title="View repair order"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation();
                                                        // Handle restore
                                                    }}
                                                    className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                                    title="Restore repair order"
                                                >
                                                    <RotateCcw className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation();
                                                        // Handle permanent delete
                                                    }}
                                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                    title="Permanently delete"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty State */}
                    {(!repairOrders || repairOrders.length === 0) && (
                        <div className="py-12 text-center">
                            <Trash2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No deleted repair orders</h3>
                            <p className="text-gray-500">
                                Deleted repair orders will appear here for recovery or permanent removal.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default DeletedRepairOrdersPage;

const repairOrderListColumns: TableColumn[] = [
    {
        name: 'repairOrderNumber',
        active: true,
        text: 'RO #',
        sortable: true,
    },
    {
        name: 'orderName',
        active: true,
        text: 'Order Name',
        sortable: true,
    },
    {
        name: 'customer',
        active: true,
        text: 'Vehicle, Customer',
        sortable: false,
    },
    {
        name: 'total',
        active: true,
        text: 'Total',
        sortable: true,
    },
    {
        name: 'due',
        active: true,
        text: 'Due',
        sortable: true,
    },
    {
        name: 'paidStatus',
        active: true,
        text: 'Paid Status',
        sortable: true,
    },
    {
        name: 'workflow',
        active: true,
        text: 'Workflow',
        sortable: true,
    },
    {
        name: 'inspectionStatus',
        active: true,
        text: 'Inspection Status',
        sortable: true,
    },
    {
        name: 'orderStatus',
        active: true,
        text: 'Order Status',
        sortable: true,
    },

    {
        name: 'authorizationStatus',
        active: true,
        text: 'Authorization Status',
        sortable: true,
    },
    {
        name: 'technicians',
        active: true,
        text: 'Technicians',
        sortable: true,
    },
    {
        name: 'dateCreated',
        active: true,
        text: 'Date Created',
        sortable: true,
    },
    {
        name: 'labels',
        active: true,
        text: 'Labels',
        sortable: true,
    },
];
